import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { ClipboardCheck, Truck, LayoutDashboard, User, Menu, X } from 'lucide-react';
import { useCurrentUser } from '@/components/auth/AuthProvider';
import { useRole } from '@/hooks/useRole';
import { NotificationBell } from '@/components/common/NotificationBell';
import { OfflineBanner } from '@/components/common/OfflineBanner';

export function AppShell() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const user = useCurrentUser();

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-800">
      {/* Header */}
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-gray-200 dark:border-gray-700 bg-wire3-navy px-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-wire3-green/20">
            <ClipboardCheck className="h-4 w-4 text-wire3-green" />
          </div>
          <span className="text-base font-bold text-white tracking-wide">
            Wire3 <span className="font-normal text-gray-400">Inspect</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          {user && (
            <span className="hidden text-sm text-gray-400 sm:block">{user.name}</span>
          )}
          <NotificationBell />
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/10 sm:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <OfflineBanner />

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 sm:hidden" onClick={() => setMenuOpen(false)} />
      )}

      <div className="flex flex-1">
        {/* Sidebar — desktop */}
        <nav className="hidden w-56 shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 sm:block">
          <NavItems />
        </nav>

        {/* Sidebar — mobile slide-out */}
        {menuOpen && (
          <nav className="fixed left-0 top-14 z-30 h-[calc(100vh-3.5rem)] w-64 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl sm:hidden">
            <NavItems onNavigate={() => setMenuOpen(false)} />
          </nav>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Bottom tab bar — mobile */}
      <nav className="sticky bottom-0 z-30 flex border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 sm:hidden">
        <BottomTab to="/" icon={<ClipboardCheck className="h-5 w-5" />} label="Inspect" />
        <BottomTab to="/vehicles" icon={<Truck className="h-5 w-5" />} label="Vehicles" />
        <AdminBottomTab />
        <BottomTab to="/profile" icon={<User className="h-5 w-5" />} label="Profile" />
      </nav>
    </div>
  );
}

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { canViewDashboard, loading } = useRole();

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-wire3-green/10 text-wire3-navy dark:bg-wire3-green/20 dark:text-wire3-green'
        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
    }`;

  return (
    <div className="flex flex-col gap-1 p-3">
      <NavLink to="/" end className={linkClass} onClick={onNavigate}>
        <ClipboardCheck className="h-5 w-5" />
        New Inspection
      </NavLink>
      <NavLink to="/vehicles" className={linkClass} onClick={onNavigate}>
        <Truck className="h-5 w-5" />
        Vehicles
      </NavLink>
      {/* Only show admin link once permissions have loaded */}
      {!loading && canViewDashboard && (
        <NavLink to="/admin" className={linkClass} onClick={onNavigate}>
          <LayoutDashboard className="h-5 w-5" />
          Admin Dashboard
        </NavLink>
      )}
      <NavLink to="/profile" className={linkClass} onClick={onNavigate}>
        <User className="h-5 w-5" />
        Profile
      </NavLink>
    </div>
  );
}

function BottomTab({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${
          isActive ? 'text-wire3-green' : 'text-gray-400 dark:text-gray-500'
        }`
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

function AdminBottomTab() {
  const { canViewDashboard, loading } = useRole();
  if (loading || !canViewDashboard) return null;
  return <BottomTab to="/admin" icon={<LayoutDashboard className="h-5 w-5" />} label="Admin" />;
}
